import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type DashboardTabAccent =
  | 'blue'
  | 'green'
  | 'purple'
  | 'tennis'
  | 'primary'
  | 'indigo'
  | 'violet'
  | 'orange';

export interface DashboardTabItem<T extends string> {
  id: T;
  label: ReactNode;
  /** Shorter label on viewports below `sm`. Falls back to `label` when omitted. */
  mobileLabel?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /** Optional count or badge after the label (e.g. list filters). */
  suffix?: ReactNode;
  /** `id` on the tab button for aria-controls pairing */
  tabId?: string;
}

export interface DashboardTabNavProps<T extends string> {
  tabs: readonly DashboardTabItem<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  accent?: DashboardTabAccent;
  /** Accessible name for the tab list */
  ariaLabel: string;
  className?: string;
  /** Apply default `mb-6` spacing below the nav (default: true) */
  withMargin?: boolean;
  role?: 'tablist';
}

const ACCENT_ACTIVE: Record<DashboardTabAccent, string> = {
  blue: 'border-blue-500 text-blue-600 dark:text-blue-400',
  green: 'border-green-600 text-green-600 dark:border-green-500 dark:text-green-400',
  purple: 'border-purple-600 text-purple-700 dark:border-purple-500 dark:text-purple-400',
  tennis: 'border-tennis-600 text-tennis-700 dark:border-tennis-500 dark:text-tennis-400',
  primary: 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400',
  indigo: 'border-indigo-600 text-indigo-700 dark:text-indigo-300',
  violet: 'border-violet-600 text-violet-700 dark:text-violet-300',
  orange: 'border-orange-600 text-orange-700 dark:text-orange-400',
};

const INACTIVE =
  'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300';

function TabLabel({
  label,
  mobileLabel,
}: {
  label: ReactNode;
  mobileLabel?: ReactNode;
}) {
  if (mobileLabel == null) {
    return <>{label}</>;
  }
  return (
    <>
      <span className="sm:hidden">{mobileLabel}</span>
      <span className="hidden sm:inline">{label}</span>
    </>
  );
}

export function DashboardTabNav<T extends string>({
  tabs,
  activeTab,
  onChange,
  accent = 'green',
  ariaLabel,
  className,
  withMargin = true,
  role,
}: DashboardTabNavProps<T>) {
  const activeClasses = ACCENT_ACTIVE[accent];

  return (
    <div
      className={cn(
        '-mx-4 border-b border-gray-200 px-4 dark:border-gray-700 sm:mx-0 sm:px-0',
        withMargin && 'mb-6',
        className
      )}
    >
      <nav
        className="-mb-px flex gap-3 overflow-x-auto overscroll-x-contain scrollbar-hide sm:gap-6"
        aria-label={ariaLabel}
        role={role}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              id={tab.tabId}
              role={role === 'tablist' ? 'tab' : undefined}
              aria-selected={role === 'tablist' ? isActive : undefined}
              disabled={tab.disabled}
              onClick={() => {
                if (!tab.disabled) {
                  onChange(tab.id);
                }
              }}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:py-2.5 sm:text-sm',
                isActive ? activeClasses : INACTIVE,
                tab.icon && '[&_svg]:h-3.5 [&_svg]:w-3.5 sm:[&_svg]:h-4 sm:[&_svg]:w-4'
              )}
            >
              {tab.icon ? (
                <span className="hidden shrink-0 sm:inline-flex" aria-hidden>
                  {tab.icon}
                </span>
              ) : null}
              <TabLabel label={tab.label} mobileLabel={tab.mobileLabel} />
              {tab.suffix ? (
                <span className="text-xs opacity-80 tabular-nums">{tab.suffix}</span>
              ) : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
